// Read-only operations logs for staff — reuses the existing security endpoints
// (a STAFF user can read them). Plus a billing collection summary computed from
// the admin invoices list.

import type { PublicPackage, PublicEmergencyAlert, PublicInvoice } from '@residenone/shared';
import { api } from './api-client';
import { listInvoices } from './invoices-api';
import { listAdminUnits } from './units-api';

export type { PublicPackage, PublicEmergencyAlert, PublicInvoice };

export interface VisitorScan {
  id: string;
  scanned_at: string;
  notes: string | null;
  scanned_by_name?: string;
  visitor_name?: string | null;
  host_name?: string | null;
}

export async function listScans(limit = 50): Promise<VisitorScan[]> {
  const { data } = await api.get<{ items: VisitorScan[] }>('/security/scans', {
    params: { limit },
  });
  return data.items ?? [];
}

export async function listOpsPackages(limit = 50): Promise<PublicPackage[]> {
  const { data } = await api.get<{ items: PublicPackage[] }>('/security/packages', {
    params: { limit },
  });
  return data.items ?? [];
}

export async function listOpsEmergency(limit = 50): Promise<PublicEmergencyAlert[]> {
  const { data } = await api.get<{ items: PublicEmergencyAlert[] }>('/security/emergency', {
    params: { limit },
  });
  return data.items ?? [];
}

// ─── Billing summary (Reports) ──────────────────────────────────────────────

export interface BillingSummary {
  total: number;
  byStatus: Record<string, number>;
  byStatusCents: Record<string, number>; // invoice totals per status
  collectedCents: number; // sum of PAID invoice totals
  outstandingCents: number; // sum of OPEN + OVERDUE invoice totals
  collectionRate: number; // collected / (collected + outstanding), 0..1
  // House-level view of the same period. A unit is "billed" once it has a live
  // invoice (OPEN/PAID/OVERDUE — DRAFT and VOID aren't real bills, matching the
  // cents math above), and "paid" once none of its invoices are still
  // OPEN/OVERDUE. Units exempt from billing (billing_override_cents = 0) never
  // get an invoice, so they're correctly absent from both counts.
  unitsBilled: number;
  unitsPaid: number;
}

// Pull every invoice (paginated). Single-cluster scale is small.
export async function fetchAllInvoices(): Promise<PublicInvoice[]> {
  const all: PublicInvoice[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < 20; i++) {
    const page = await listInvoices({ limit: 100, ...(cursor ? { cursor } : {}) });
    all.push(...page.items);
    if (!page.nextCursor) break;
    cursor = page.nextCursor;
  }
  return all;
}

export type BillingPeriod = string; // 'all' for all-time, 'YYYY' for a year, or 'YYYY-MM' (filter by issue date)

/** Current calendar month as 'YYYY-MM' (matches getUnitBilling's thisMonth basis). */
export function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

/** Years from the earliest invoice issue-year up to the current year, newest first. */
export function billingYearRange(invoices: PublicInvoice[]): number[] {
  const now = new Date().getUTCFullYear();
  let min = now;
  for (const inv of invoices) {
    const y = Number(inv.issue_date.slice(0, 4));
    if (Number.isFinite(y) && y < min) min = y;
  }
  const years: number[] = [];
  for (let y = now; y >= min; y--) years.push(y);
  return years;
}

/** Aggregate invoices issued in `period` ('all' = every invoice). Status-based. */
export function summarizeInvoices(invoices: PublicInvoice[], period: BillingPeriod): BillingSummary {
  // A prefix match covers both a month ('2026-10') and a whole year ('2026').
  const rows = period === 'all' ? invoices : invoices.filter((i) => i.issue_date.startsWith(period));
  const byStatus: Record<string, number> = {};
  const byStatusCents: Record<string, number> = {};
  let collectedCents = 0;
  let outstandingCents = 0;
  // unit_id → does it still owe anything this period?
  const unitOwes = new Map<string, boolean>();
  for (const inv of rows) {
    byStatus[inv.status] = (byStatus[inv.status] ?? 0) + 1;
    byStatusCents[inv.status] = (byStatusCents[inv.status] ?? 0) + inv.total_cents;
    if (inv.status === 'PAID') collectedCents += inv.total_cents;
    else if (inv.status === 'OPEN' || inv.status === 'OVERDUE') outstandingCents += inv.total_cents;

    if (inv.status === 'PAID' || inv.status === 'OPEN' || inv.status === 'OVERDUE') {
      const owes = inv.status === 'OPEN' || inv.status === 'OVERDUE';
      // Once a unit owes on any invoice it stays unpaid, however many others
      // it has settled.
      unitOwes.set(inv.unit_id, (unitOwes.get(inv.unit_id) ?? false) || owes);
    }
  }
  const denom = collectedCents + outstandingCents;
  let unitsPaid = 0;
  for (const owes of unitOwes.values()) if (!owes) unitsPaid++;
  return {
    total: rows.length,
    byStatus,
    byStatusCents,
    collectedCents,
    outstandingCents,
    collectionRate: denom > 0 ? collectedCents / denom : 0,
    unitsBilled: unitOwes.size,
    unitsPaid,
  };
}

export async function getBillingSummary(period: BillingPeriod = 'all'): Promise<BillingSummary> {
  return summarizeInvoices(await fetchAllInvoices(), period);
}

// ─── Per-unit billing / arrears ─────────────────────────────────────────────

export type ThisMonthStatus = 'PAID' | 'UNPAID' | 'NONE';

export interface UnitBilling {
  unit_id: string;
  block: string;
  number: string;
  label: string;
  thisMonth: ThisMonthStatus; // current month's invoice
  unpaidCount: number; // OPEN + OVERDUE invoices = months behind
  outstandingCents: number; // remaining owed across unpaid invoices
  oldestUnpaidMonth: string | null; // "YYYY-MM"
  invoices: PublicInvoice[]; // all (non-void), newest first
}

const monthOf = (dateStr: string) => dateStr.slice(0, 7); // "YYYY-MM"

export async function getUnitBilling(): Promise<UnitBilling[]> {
  const [units, invoices] = await Promise.all([listAdminUnits(), fetchAllInvoices()]);
  return unitBillingFrom(units, invoices);
}

/** getUnitBilling's per-unit view from lists already in hand (no requests). */
export function unitBillingFrom(
  units: Awaited<ReturnType<typeof listAdminUnits>>,
  invoices: PublicInvoice[],
): UnitBilling[] {
  const byUnit = new Map<string, PublicInvoice[]>();
  for (const inv of invoices) {
    if (inv.status === 'VOID') continue;
    const arr = byUnit.get(inv.unit_id) ?? [];
    arr.push(inv);
    byUnit.set(inv.unit_id, arr);
  }
  const thisMonth = new Date().toISOString().slice(0, 7);
  return units.map((u) => {
    const invs = (byUnit.get(u.id) ?? [])
      .slice()
      .sort((a, b) => b.issue_date.localeCompare(a.issue_date));
    const unpaid = invs.filter((i) => i.status === 'OPEN' || i.status === 'OVERDUE');
    const monthInvs = invs.filter((i) => monthOf(i.issue_date) === thisMonth);
    const tm: ThisMonthStatus =
      monthInvs.length === 0
        ? 'NONE'
        : monthInvs.some((i) => i.status === 'OPEN' || i.status === 'OVERDUE')
          ? 'UNPAID'
          : 'PAID';
    const outstandingCents = unpaid.reduce(
      (s, i) => s + Math.max(0, i.total_cents - i.paid_cents),
      0,
    );
    const oldestUnpaidMonth = unpaid.length
      ? unpaid.map((i) => monthOf(i.issue_date)).sort()[0]!
      : null;
    return {
      unit_id: u.id,
      block: u.block,
      number: u.number,
      label: u.label,
      thisMonth: tm,
      unpaidCount: unpaid.length,
      outstandingCents,
      oldestUnpaidMonth,
      invoices: invs,
    };
  });
}
